import { Observable } from '@disy/lib/event-util';
import { compareWithLocale } from '@disy/lib/locale';
import { logger } from '@disy/lib/logging';

import { hasPrivilege } from 'cadenza/access-management/model/object-privilege-util';
import { getNavigatorTree } from 'cadenza/navigator/navigator-tree-api';
import type { UnprefixedSpecialFilterToken } from 'cadenza/navigator/navigator-tree-service/navigator-tree-filter';
import { NavigatorTreeFilter } from 'cadenza/navigator/navigator-tree-service/navigator-tree-filter';
import { filterNavigatorTree, findNode } from 'cadenza/navigator/navigator-tree-util';
import { getNavigatorFilterItemType } from 'cadenza/navigator/navigator-tree/nav-tree-util';
import type { NavigatorTreeNodeDto } from 'cadenza/repository/model/navigator-tree-node-dto';
import { isParentNode, isProjectNode, isWorksheetNode } from 'cadenza/repository/model/navigator-tree-node-dto';
import type { RepositoryItemId } from 'cadenza/repository/model/repository-item-id';
import { getSubtreeLabels } from 'cadenza/workbook/repository-tree/repository-tree-utils';

export const EVENT_FILTERING_CHANGE = 'change:filtering';
export const EVENT_TREE_CHANGE = 'change:tree';

let navigatorTreeService: Promise<NavigatorTreeService>;
let projectTreeService: NavigatorTreeService;

/**
 * @deprecated The NavigatorTreeService caches the navigator tree data in memory, which is bad,
 * because it conflicts with browser caching. Browser caching is preferable because we can implement
 * it so that the cache is also invalidated if another client makes changes to the navigator tree.
 *
 * So you should use the web-services directly instead and maybe extract some filter methods here.
 *
 * Exception: A valid use case for still using this helper function for getting the {@link NavigatorTreeService}
 * is handling the content of the {@link import('cadenza/navigator/navigator-tree/nav-tree').NavTree},
 * i.e. to reload the navigatorTree by using {@link NavigatorTreeService#reload} - This is a valid use case,
 * because it would require the navigator to listen to invalidation events from the server, and we do not have that, yet.
 */
export function getNavigatorTreeService() {
  if (!navigatorTreeService) {
    navigatorTreeService = getNavigatorTree().then((tree) => new NavigatorTreeService(tree));
  }
  return navigatorTreeService;
}

export async function reloadNavigatorTreeService() {
  return await (await getNavigatorTreeService()).reload();
}

export function setProjectTreeService(service: NavigatorTreeService) {
  projectTreeService = service;
}

/**
 * @deprecated The NavigatorTreeService caches the repository tree data in memory, which is bad,
 * because it conflicts with browser caching. Browser caching is preferable because we can implement
 * it so that the cache is also invalidated if another client makes changes to the navigator tree.
 *
 * So you should use the web-services directly instead and maybe extract some filter methods here.
 */
export function getProjectTreeService() {
  return projectTreeService;
}

interface Select2Tag {
  id: string;
  text: string;
  specialTag?: true;
}

/**
 * @deprecated The NavigatorTreeService caches the navigator tree data in memory, which is bad,
 * because it conflicts with browser caching. Browser caching is preferable because we can implement
 * it so that the cache is also invalidated if another client makes changes to the navigator tree.
 *
 * So you should use the web-services directly instead and maybe extract some filter methods here.
 */
export class NavigatorTreeService extends Observable {
  // ! because it is initialized in the constructor
  _tree!: NavigatorTreeNodeDto;
  // ! because it is initialized in the constructor
  _filteredTree!: NavigatorTreeNodeDto;
  // ! because it is initialized in the constructor
  _specialTokensInTree!: Set<UnprefixedSpecialFilterToken>;
  _filter = NavigatorTreeFilter.of([]);
  #getNavigatorTreeFunction: () => Promise<NavigatorTreeNodeDto>;

  constructor(
    tree: NavigatorTreeNodeDto,
    {
      getNavigatorTreeFunction = getNavigatorTree,
    }: {
      getNavigatorTreeFunction?: () => Promise<NavigatorTreeNodeDto>;
    } = {},
  ) {
    super();
    this._setTree(tree);
    this.#getNavigatorTreeFunction = getNavigatorTreeFunction;
  }

  async reload() {
    try {
      this._setTree(await this.#getNavigatorTreeFunction());
    } catch (error) {
      logger.error('Failed to refresh the navigation tree', error);
      throw error;
    }
  }

  getAllLabels() {
    // local-specific sort
    return [...new Set(getSubtreeLabels(this._tree))].sort(compareWithLocale);
  }

  getAllLabelsAsSelect2Tags(): Select2Tag[] {
    return this.getAllLabels().map((label) => ({
      id: label,
      text: label,
      specialTag: true,
    }));
  }

  get navigatorTree() {
    return structuredClone(this._tree);
  }

  _setTree(navigatorTree: NavigatorTreeNodeDto) {
    this._tree = filterNavigatorTree(navigatorTree, [
      'WORKSHEET',
      'WORKBOOK_VIEW',
      'PROJECT',
      'EXTERNAL_LINK_EXTENSION',
    ]);
    this._specialTokensInTree = collectSpecialTokensInTree(this._tree);
    this._updateFilteredTree();
  }

  setFilterTokens(filterTokens: string[]) {
    const oldFilter = this._filter;
    this._filter = NavigatorTreeFilter.of(filterTokens);

    if (oldFilter.empty !== this._filter.empty) {
      this.dispatchEvent(new CustomEvent(EVENT_FILTERING_CHANGE));
    }

    this._updateFilteredTree();
  }

  _updateFilteredTree() {
    this._filteredTree = createFilteredTree(this._tree, this._filter);
    this.dispatchEvent(new CustomEvent(EVENT_TREE_CHANGE));
  }

  sortSelectData(data: Select2Tag[]) {
    return data.sort((a, b) => {
      if (a.specialTag === b.specialTag) {
        return a.text.localeCompare(b.text);
      }
      return a.specialTag ? 1 : -1;
    });
  }

  get filteredNavigatorTree() {
    return this._filteredTree;
  }

  get filterEmpty() {
    return this._filter.empty;
  }

  get specialFilterTokens() {
    return [...this._specialTokensInTree];
  }

  getTreeNode(itemId: RepositoryItemId) {
    return findNode(this.navigatorTree, itemId);
  }
}

function createFilteredTree(rootNode: NavigatorTreeNodeDto, filter: NavigatorTreeFilter) {
  const rootCopy: NavigatorTreeNodeDto & { children?: NavigatorTreeNodeDto[] } = removeInvisibleWorksheets({
    ...rootNode,
  });
  rootCopy.children =
    isParentNode(rootNode) ? rootNode.children.flatMap((child) => createFilteredSubtree(child, filter)) : [];
  return rootCopy;
}

function removeInvisibleWorksheets(node: NavigatorTreeNodeDto) {
  if (isParentNode(node)) {
    node.children = node.children
      .map(removeInvisibleWorksheets)
      // Only nodes that represent worksheets have the worksheetVisible flag.
      .filter((childNode) => !isWorksheetNode(childNode) || childNode.worksheetVisible);
  }
  return node;
}

function createFilteredSubtree(node: NavigatorTreeNodeDto, filter: NavigatorTreeFilter, forceIncludeAll = false) {
  const match = filter.includesNode(node);
  const nodeIncluded = forceIncludeAll || match.nodeIncluded;

  const copy: NavigatorTreeNodeDto & { children?: NavigatorTreeNodeDto[] } = { ...node };

  // Special behavior for projects: don't show children by default when there is no filter,
  // children are only shown when they actually match a filter
  if (isProjectNode(node) && !filter.terms.length) {
    copy.children = [];
    return [copy];
  }

  // for matching nodes, we force all the children to be visible, but we still need to apply the filter on them
  // in order to get the matching info for rendering (labels etc.)
  const copiedChildren =
    isParentNode(node) ? node.children.flatMap((child) => createFilteredSubtree(child, filter, nodeIncluded)) : [];

  // non-matching nodes are included only if they have matching children
  if (!nodeIncluded && !copiedChildren?.length) {
    return [];
  }

  copy.children = copiedChildren?.length ? copiedChildren : undefined;

  if (!filter.empty) {
    // store the matching info on the node, it will be used to expand the node, highlight text etc.
    copy.filterMatch = {
      terms: filter.terms,
      matchingLabels: match.matchingLabels,
    };
  }
  return [copy];
}

/**
 * Recursive function to collect all special tokens in the (sub)tree defined by its root node passed
 * in as parameter.
 *
 * @param node - root node of the (sub)tree of which the special tokens are extracted
 * @param [specialTokensInTree] - set of special tokens, gets filled by this function
 */
function collectSpecialTokensInTree(
  node: NavigatorTreeNodeDto,
  specialTokensInTree = new Set<UnprefixedSpecialFilterToken>(),
) {
  // WITH_LABEL
  if ((node.labels ?? []).length > 0) {
    specialTokensInTree.add('WITH_LABEL');
  }

  // SHARED
  if (hasPrivilege('privileges' in node ? node.privileges : null, 'share')) {
    specialTokensInTree.add('SHARED');
  }

  // Type special filter tokens
  const filterType = getNavigatorFilterItemType(node);
  if (filterType) {
    specialTokensInTree.add(filterType);
  }

  if (isParentNode(node)) {
    node.children.forEach((child) => collectSpecialTokensInTree(child, specialTokensInTree));
  }

  return specialTokensInTree;
}
